import { GridLayout } from '@/features/storyblok/constants';
import type { SbBlokData, SbRichTextDoc } from '@storyblok/vue';

export enum LinkType {
  STORYBLOK = 'storyblok',
  WARD = 'ward',
  EXTERNAL = 'external',
}

export interface StoryblokAsset {
  id: number;
  filename: string;
  alt: string;
  is_external_url: boolean;
}

export interface StoryblokBannerBlok {
  richText: SbRichTextDoc | string;
  linkType: LinkType;
  url: StoryblokLinkBlok;
  buttonTitle: string;
  bannerStyle: 'contrast' | 'normal';
}

export interface StoryblokButtonBlok {
  title: string;
  linkType: LinkType;
  buttonLink: StoryblokLinkBlok;
  position: 'left' | 'right' | 'center';
  style: 'primary' | 'secondary' | 'standard';
}

export interface StoryblokCardBlok {
  title?: string;
  subtitle?: string;
  text?: SbRichTextDoc | string;
  image?: StoryblokImageBlok;
  imageBackground?: boolean;
  textColorContrast?: boolean;
  imagePosition?: 'top' | 'bottom' | 'left' | 'right';
  imageAlt?: string;
  buttonTitle?: string;
  linkType?: LinkType;
  link?: StoryblokLinkBlok;
  buttonPosition?: 'left' | 'right' | 'center';
  buttonStyle?: 'primary' | 'secondary' | 'standard';
}

export interface StoryblokCarouselBlok {
  title?: string;
  displayMode?: 'card' | 'slider';
  carouselItems?: SbBlokData[];
  cards?: SbBlokData[];
  slides?: SbBlokData[];
  items?: SbBlokData[];
}

export interface StoryblokFaqBlok {
  questions?: SbBlokData[];
  Questions?: SbBlokData[];
}

export interface StoryblokFaqEntryBlok {
  question?: SbRichTextDoc | string;
  answer?: SbRichTextDoc | string;
}

export interface StoryblokGridBlok {
  layout?: GridLayout;
  columns?: SbBlokData[];
  column1?: SbBlokData[];
  column2?: SbBlokData[];
  column3?: SbBlokData[];
  column4?: SbBlokData[];
}

export interface StoryblokImageBlok {
  alt: string;
  filename: string;
  name: string;
  is_external_url?: boolean;
}

export interface StoryblokLinkBlok {
  cached_url: string;
  fieldtype: string;
  id: string;
  linktype: string;
  url: string;
}

export interface StoryblokPageBlok {
  body?: SbBlokData[];
  content?: SbBlokData[];
}

export interface StoryblokSectionBlok extends StoryblokPageBlok {
  title?: string;
  displayTitle?: boolean;
  displayBorder?: boolean;
}

export interface StoryblokPageHeaderBlok {
  title?: string;
  subtitle?: string;
  image?: StoryblokImageBlok;
}

export interface StoryblokPreviewImageBlok {
  asset?: StoryblokAsset;
  description?: string;
  title?: string;
}

export interface StoryblokRichTextBlok {
  content: SbRichTextDoc;
}

export interface StoryblokSectionBlok {
  title?: string;
  size: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  titleStyle: string[];
  displayTitle?: boolean;
  displayBorder?: boolean;
  content?: SbBlokData[];
  body?: SbBlokData[];
}

export interface StoryblokSectionItemBlok {
  title?: string;
  size?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  titleBackground?: boolean;
  backgroundImage?: StoryblokImageBlok;
  linkType?: LinkType;
  link?: StoryblokLinkBlok;
}

export interface StoryblokTitleBlok {
  title: string;
  size: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  titleStyle: string[];
}

export interface StoryblokTextIllustratedBlok {
  grid?: GridLayout;
  text: SbRichTextDoc | string;
  image?: StoryblokImageBlok;
  imageAlt?: string;
  imagePosition?: 'left' | 'right' | 'top' | 'bottom';
}

export interface StoryblokVideoBlok {
  title?: string;
  file: StoryblokLinkBlok;
  aspectRatio?: string;
  ratio?: string;
  autoplay?: boolean;
}
